import { useState, useMemo, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  login,
  loginTotp,
  type LoginResult,
  type LoginTotpResult,
} from '../api/login';
import {
  resolveReturnUrl,
  isAbsoluteReturnUrl,
  loginDefaultReturnUrl,
} from '../utils/returnUrl';

// NOTE: copy here is functional/placeholder — voice + UX polish is uid-001-11
// (Menley), explicitly OUT of this story. Per conventions/frontend-patterns.md,
// navigation uses <Link> (not <button onClick={navigate}>) and every
// interactive element carries a data-testid.
//
// MIRRORS RegisterPage structure: same return-origin capture/validation (the
// shared resolveReturnUrl open-redirect gate — NO hand-rolled origin check),
// same email/password form shape, same reuse of the `register` CSS classes
// (visual polish is uid-001-11), same window.location.assign redirect handling.

// bl-1523: identity-service redirects a failed OAuth sign-in to
// `?oauth_error=<code>`. 7 codes collapse into 3 member-meaningful buckets
// (copy ratified by Menley 2026-10-12). The raw code is NEVER shown. Unknown
// codes fall back to the try-again bucket.
//
// Bucket 3 (`already_linked` / `no_account`) is deliberately privacy-neutral: it
// must not confirm or deny that an account exists or is linked elsewhere.
// PENDING one all-three privacy glance before the wording is final.
type OauthErrorBucket = 'try-again' | 'verify-email' | 'account-problem';

const OAUTH_ERROR_BUCKET: Record<string, OauthErrorBucket> = {
  invalid_callback: 'try-again',
  invalid_state: 'try-again',
  exchange_failed: 'try-again',
  provider_error: 'try-again',
  verify_email: 'verify-email',
  already_linked: 'account-problem',
  no_account: 'account-problem',
};

const OAUTH_ERROR_MESSAGE: Record<OauthErrorBucket, string> = {
  'try-again': 'We couldn\u2019t finish signing you in. Please try again.',
  'verify-email':
    'Please verify your email first, then sign in again. Check your inbox for the link.',
  'account-problem':
    'We couldn\u2019t sign you in with that account. If this keeps happening, contact support.',
};

type LoginStatus = 'idle' | 'submitting' | LoginResult['kind'];
type TotpStatus = 'idle' | 'submitting' | LoginTotpResult['kind'];

export default function LoginPage() {
  const [searchParams] = useSearchParams();

  // Capture + validate the return-to-origin target once, on mount. Accepts both
  // `return_origin` (the server's field name) and `returnUrl`/`redirect_uri`
  // (RegisterPage's params) so a suite can use either name. Validated through the
  // SAME shared open-redirect gate RegisterPage uses (resolveReturnUrl) — a
  // non-allowlisted origin collapses to the safe default, never reaching the wire.
  //
  // DIRECT-LOGIN DEFAULT (no param at all): fall back to the DAO portal — the
  // member's home — NOT the `/register` front-door. The portal default is an
  // allowlisted absolute origin, so `returnOrigin` (below) resolves to it and the
  // `?code=` cross-domain handoff fires → the member lands logged in. An EXPLICIT
  // but evil returnUrl still collapses safely via resolveReturnUrl; only the
  // truly-absent case takes the portal default.
  const returnTarget = useMemo(() => {
    const raw =
      searchParams.get('return_origin') ??
      searchParams.get('returnUrl') ??
      searchParams.get('redirect_uri');
    if (raw == null) {
      return loginDefaultReturnUrl();
    }
    return resolveReturnUrl(raw);
  }, [searchParams]);

  // The `return_origin` we send to /login is ONLY a cross-origin allowlisted
  // absolute ORIGIN (scheme+host[:port], NO path/trailing-slash) — the server's
  // allowlist is origin-string-exact, so we send `URL.origin` not the full URL.
  // When the resolved target is relative (same-app), we send no return_origin
  // (a direct/central-origin login) and there is no cross-domain code.
  const returnOrigin = useMemo(() => {
    if (!isAbsoluteReturnUrl(returnTarget)) {
      return undefined;
    }
    try {
      return new URL(returnTarget).origin;
    } catch {
      return undefined;
    }
  }, [returnTarget]);

  const oauthErrorBucket = useMemo<OauthErrorBucket | null>(() => {
    const code = searchParams.get('oauth_error');
    if (!code) return null;
    return Object.prototype.hasOwnProperty.call(OAUTH_ERROR_BUCKET, code)
      ? OAUTH_ERROR_BUCKET[code]
      : 'try-again';
  }, [searchParams]);
  const [oauthErrorDismissed, setOauthErrorDismissed] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<LoginStatus>('idle');
  const [retryAfter, setRetryAfter] = useState<number | undefined>();

  // 2FA step state. When the first /login returns mfa_required we stash the
  // single-use mfa_token and switch the form to the TOTP-code step.
  const [mfaToken, setMfaToken] = useState<string | undefined>();
  const [totpCode, setTotpCode] = useState('');
  const [totpStatus, setTotpStatus] = useState<TotpStatus>('idle');

  // The redirect target after a successful login. For a non-2FA cross-domain
  // login the SERVER returns `<return_origin>?code=<code>` (the single-use handoff
  // code the consuming suite exchanges). We assign to exactly that. For a relative
  // (same-app) login there is no code → fall back to the resolved relative target.
  function redirectAfterSuccess(serverRedirect?: string) {
    if (serverRedirect) {
      window.location.assign(serverRedirect);
      return;
    }
    // No server redirect (direct login, no return_origin) → honor the resolved
    // relative return target so the person lands back where they came from.
    window.location.assign(returnTarget);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setRetryAfter(undefined);

    // Minimal presence check (the server is authoritative on credentials; we do
    // NOT mirror password-strength rules on LOGIN — an existing password may
    // predate a rule change). Empty fields just don't submit.
    if (!email || !password) {
      setStatus('unauthorized');
      return;
    }

    setStatus('submitting');
    const result = await login({
      email,
      password,
      return_origin: returnOrigin,
    });

    if (result.kind === 'mfa') {
      // 2FA challenge — stash the token + switch to the TOTP-code step.
      setMfaToken(result.mfaToken);
      setStatus('mfa');
      return;
    }

    if (result.kind === 'success') {
      setStatus('success');
      redirectAfterSuccess(result.redirect);
      return;
    }

    if (result.kind === 'locked') {
      setRetryAfter(result.retryAfter);
    }
    setStatus(result.kind);
  }

  async function handleTotpSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mfaToken || !totpCode) {
      setTotpStatus('invalidCode');
      return;
    }

    setTotpStatus('submitting');
    const result = await loginTotp({ mfa_token: mfaToken, code: totpCode });

    if (result.kind === 'success') {
      setTotpStatus('success');
      // bl-1048: a 2FA cross-domain login now completes the SAME one-time `?code=`
      // handoff as the non-2FA path. The server mints the code on `/login/totp`
      // (bl-768 carried the validated `return_origin` through the mfa challenge),
      // and `loginTotp` surfaces it as `result.redirect`. Reuse the shared
      // redirectAfterSuccess — identical to the non-2FA `/login` success branch —
      // which honors the server-minted `<origin>?code=` and falls back to the
      // locally-resolved return target when there's no server redirect (a
      // direct/central 2FA login with no `return_origin`).
      redirectAfterSuccess(result.redirect);
      return;
    }

    setTotpStatus(result.kind);
  }

  // ── 2FA TOTP-code step ────────────────────────────────────────────────────
  if (status === 'mfa') {
    return (
      <main className="register" data-testid="login-page">
        <section className="register__card">
          <h1 className="register__title">Enter your code</h1>
          <p className="register__subtitle">
            Enter the 6-digit code from your authenticator app.
          </p>

          {totpStatus === 'invalidCode' && (
            <p
              className="register__banner register__banner--block"
              data-testid="login-totp-error-invalid"
            >
              That code didn&apos;t work. It may have expired. Try again.
            </p>
          )}
          {totpStatus === 'error' && (
            <p
              className="register__banner register__banner--block"
              data-testid="login-totp-error-generic"
            >
              Something went wrong on our end. Give it another try.
            </p>
          )}

          <form
            onSubmit={handleTotpSubmit}
            noValidate
            data-testid="login-totp-form"
          >
            <label className="register__field">
              <span>Authentication code</span>
              <input
                type="text"
                name="totp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                data-testid="login-totp-code"
              />
            </label>

            <button
              type="submit"
              className="register__submit"
              disabled={totpStatus === 'submitting'}
              data-testid="login-totp-submit"
            >
              {totpStatus === 'submitting' ? 'Verifying…' : 'Verify code'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  // ── Email + password step ──────────────────────────────────────────────────
  return (
    <main className="register" data-testid="login-page">
      <section className="register__card">
        <h1 className="register__title">Sign in</h1>
        <p className="register__subtitle">
          Sign in to Hello World DAO and FounderyOS.
        </p>

        {oauthErrorBucket && !oauthErrorDismissed && (
          <div
            className="register__banner register__banner--block"
            role="alert"
            data-testid="login-oauth-error-card"
            data-bucket={oauthErrorBucket}
          >
            <p data-testid="login-oauth-error-message">
              {OAUTH_ERROR_MESSAGE[oauthErrorBucket]}
            </p>
            <button
              type="button"
              onClick={() => setOauthErrorDismissed(true)}
              aria-label="Dismiss"
              data-testid="login-oauth-error-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}
        {status === 'unauthorized' && (
          <p
            className="register__banner register__banner--block"
            data-testid="login-error-unauthorized"
          >
            That email or password didn&apos;t match. Give it another try.
          </p>
        )}
        {status === 'locked' && (
          <p
            className="register__banner register__banner--block"
            data-testid="login-error-locked"
          >
            Too many attempts.{' '}
            {retryAfter
              ? `Please try again in ${retryAfter} seconds.`
              : 'Please try again later.'}
          </p>
        )}
        {status === 'error' && (
          <p
            className="register__banner register__banner--block"
            data-testid="login-error-generic"
          >
            Something went wrong on our end. Give it another try.
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate data-testid="login-form">
          <label className="register__field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
            />
          </label>

          <label className="register__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
          </label>

          <button
            type="submit"
            className="register__submit"
            disabled={status === 'submitting'}
            data-testid="login-submit"
          >
            {status === 'submitting' ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="register__footer">
          <Link to="/forgot-password" data-testid="login-forgot-password-link">
            Forgot your password?
          </Link>
        </p>

        <p className="register__footer">
          Need an account?{' '}
          <Link to="/register" data-testid="login-register-link">
            Create one
          </Link>
        </p>
      </section>
    </main>
  );
}
